import { openChatCommand } from '../../ai-chat/lib/chatCommandHandoff.js';
import { commandRequest } from '../../../agents/context/ui-command-registry.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Popconfirm, Space, Tooltip, Typography } from 'antd';
import type { ColumnType } from 'antd/es/table/interface';
import {
  CheckSquareOutlined,
  DisconnectOutlined,
  EditOutlined,
  PlusOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';

import { formatDateTime, formatPersonName } from '../../../shared/format/index.js';
import { useCurrentUser } from '../../../shared/scope/useCurrentScope.js';
import { EntityHistoryButton } from '../../activity/components/EntityHistoryButton.js';
import { DeleteEntityButton } from '../../../shared/ui/DeleteEntityButton.js';
import { EntityLink, type EntityKind } from '../../../shared/ui/EntityLink.js';
import { EntityTable } from '../../../shared/ui/EntityTable.js';
import { GridColumnsButton } from '../../../shared/grid/GridColumnsButton.js';
import { useGridProfile, type GridColumnMeta } from '../../../shared/grid/useGridProfile.js';
import { useOrderedColumns } from '../../../shared/grid/useOrderedColumns.js';
import { byDate, byText } from '../../../shared/ui/tableSorters.js';
import type { NoteItem, NoteLinkInput, NoteLinkItem } from '../api/notes.client.js';
import { NoteFormModal } from '../components/NoteFormModal.js';
import { NoteCreateModal } from '../components/NoteCreateModal.js';
import { NoteShareModal } from '../components/NoteShareModal.js';
import { NoteVisibilityTag } from '../components/NoteVisibilityTag.js';
import { useDeleteNoteMutation, useNotes, useUpdateNoteMutation } from '../hooks/useNotes.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { FilterBar, FilterSearch } from '../../../shared/ui/FilterBar.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LIST_CREATE_BUTTON_STYLE, LIST_SURFACE_STYLE } from '../../../shared/ui/list-styles.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';
import {
  FLEX_TITLE_CELL_CLASS,
  tableMinWidth as computeTableMinWidth,
} from '../../../shared/ui/table-width.js';
import '../components/notes-list.css';

const { Text } = Typography;

const NOTE_GRID_COLUMNS: GridColumnMeta[] = [
  {
    key: 'body',
    get label() {
      return i18n.t('crm.features.columns.note');
    },
  },
  {
    key: 'links',
    get label() {
      return i18n.t('crm.features.columns.links');
    },
  },
  {
    key: 'author',
    get label() {
      return i18n.t('crm.features.columns.author');
    },
  },
  {
    key: 'createdAt',
    get label() {
      return i18n.t('crm.features.columns.created_at');
    },
  },
];

/** Оставшиеся привязки в формате, который принимает API (для замены набора). */
function toLinkInputs(links: NoteLinkItem[]): NoteLinkInput[] {
  return links.flatMap((link): NoteLinkInput[] => {
    if (link.dealId) return [{ dealId: link.dealId }];
    if (link.companyId) return [{ companyId: link.companyId }];
    if (link.contactId) return [{ contactId: link.contactId }];
    if (link.taskId) return [{ taskId: link.taskId }];
    if (link.calendarEventId) return [{ calendarEventId: link.calendarEventId }];
    if (link.documentId) return [{ documentId: link.documentId }];
    if (link.templateId) return [{ templateId: link.templateId }];
    if (link.projectId) return [{ projectId: link.projectId }];
    return [];
  });
}

/** Привязка → параметры для общей ссылки-тега. */
function linkTarget(link: NoteLinkItem): { kind: EntityKind; id: string; label: string } | null {
  if (link.deal) return { kind: 'deal', id: link.deal.id, label: link.deal.title };
  if (link.contact) {
    return {
      kind: 'contact',
      id: link.contact.id,
      label: formatPersonName(link.contact.firstName, link.contact.lastName),
    };
  }
  if (link.company) return { kind: 'company', id: link.company.id, label: link.company.name };
  if (link.task) return { kind: 'task', id: link.task.id, label: link.task.title };
  if (link.calendarEvent) {
    return { kind: 'meeting', id: link.calendarEvent.id, label: link.calendarEvent.title };
  }
  if (link.document) return { kind: 'document', id: link.document.id, label: link.document.title };
  if (link.template) return { kind: 'template', id: link.template.id, label: link.template.name };
  if (link.project) return { kind: 'project', id: link.project.id, label: link.project.name };
  return null;
}

export default function NotesPage(): ReactElement {
  useI18nSubscription();
  const currentUser = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  // Поиск живёт в URL: ссылку на отфильтрованный список можно передать.
  const q = searchParams.get('q') ?? '';
  const activeQuery = q.trim();
  const { data, isLoading, isError, error, refetch } = useNotes(
    activeQuery ? { q: activeQuery } : {},
  );
  const updateMut = useUpdateNoteMutation();
  const deleteMut = useDeleteNoteMutation();
  const setQuery = (value: string): void => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === '') next.delete('q');
        else next.set('q', value);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  // Правка идёт общей формой заметки — в ячейке грида её больше нет.
  const [editingNote, setEditingNote] = useState<NoteItem | null>(null);
  const [sharingNote, setSharingNote] = useState<NoteItem | null>(null);
  // Создание — та же модалка, что у плавающей кнопки «Заметка».
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();

  const notes = data ?? [];

  /** Автор заметки — владелец доступа: кнопка «Поделиться» только у него. */
  // While the current user is still loading both sides are undefined and
  // undefined === undefined would briefly show "Share" on other people's notes.
  const isOwnNote = (note: NoteItem): boolean =>
    Boolean(currentUser) && note.createdByMember?.userId === currentUser?.id;

  const handleExtractTasks = (note: NoteItem): void => {
    openChatCommand(navigate, commandRequest('extractTasksFromNote', { noteId: note.id }));
  };

  /** Снять одну привязку — отправляем оставшийся набор. */
  const removeLink = (note: NoteItem, linkId: string): void => {
    updateMut.mutate({
      id: note.id,
      input: { links: toLinkInputs(note.links.filter((link) => link.id !== linkId)) },
    });
  };

  const columnMap: Record<string, ColumnType<NoteItem>> = {
    body: {
      title: i18n.t('crm.features.features.notes.pages.notespage.заметка'),
      key: 'body',
      sorter: byText<NoteItem>((note) => note.body),
      render: (_, note) => (
        <span className={`note-grid-body ${FLEX_TITLE_CELL_CLASS} text-gray-800`} title={note.body}>
          {note.body}
        </span>
      ),
    },
    links: {
      // Все привязки одной заметки — в одной строке; крестик снимает конкретную.
      title: i18n.t('crm.features.features.notes.pages.notespage.привязки'),
      key: 'links',
      width: 260,
      render: (_, note) =>
        note.links.length === 0 ? (
          <Text type="secondary">
            {i18n.t('crm.features.features.notes.pages.notespage.без_привязки')}
          </Text>
        ) : (
          <div className="flex flex-wrap gap-1">
            {note.links.map((link) => {
              const target = linkTarget(link);
              if (!target) return null;
              return (
                <span key={link.id} className="inline-flex items-center">
                  <EntityLink
                    kind={target.kind}
                    id={target.id}
                    label={target.label}
                    withKindPrefix
                  />
                  <Button
                    size="small"
                    type="text"
                    aria-label={i18n.t('crm.features.dynamic.features.notes.pages.notespage.71', {
                      p0: target.label,
                    })}
                    title={i18n.t('crm.features.features.notes.pages.notespage.снять_эту_привязку')}
                    onClick={() => removeLink(note, link.id)}
                  >
                    ×
                  </Button>
                </span>
              );
            })}
          </div>
        ),
    },
    author: {
      title: i18n.t('crm.features.features.notes.pages.notespage.автор'),
      key: 'author',
      width: 180,
      sorter: byText<NoteItem>((note) => note.createdByMember?.user.name),
      render: (_, note) => (
        <span className="inline-flex items-center">
          {note.createdByMember?.user.name ?? '—'}
          <NoteVisibilityTag visibility={note.visibility ?? 'workspace'} />
        </span>
      ),
    },
    createdAt: {
      title: i18n.t('crm.features.features.notes.pages.notespage.дата_создания'),
      key: 'createdAt',
      width: 150,
      defaultSortOrder: 'descend',
      sorter: byDate<NoteItem>((note) => note.createdAt),
      render: (_, note) => <span className="text-gray-500">{formatDateTime(note.createdAt)}</span>,
    },
  };

  const actionsColumn: ColumnType<NoteItem> = {
    title: i18n.t('crm.features.features.notes.pages.notespage.действия'),
    key: 'actions',
    width: 190,
    render: (_, note) => (
      <Space size={4}>
        <Tooltip
          title={i18n.t('crm.features.features.notes.pages.notespage.извлечь_задачи_в_план')}
        >
          <Button
            size="small"
            type="text"
            data-testid="note-extract-tasks-btn"
            aria-label={i18n.t('crm.features.features.notes.pages.notespage.извлечь_задачи_в_план')}
            icon={<CheckSquareOutlined />}
            onClick={() => handleExtractTasks(note)}
          />
        </Tooltip>
        {isOwnNote(note) ? (
          <Tooltip
            title={i18n.t(
              'crm.features.features.notes.components.notesharemodal.поделиться_заметкой',
            )}
          >
            <Button
              size="small"
              type="text"
              data-testid="note-share-btn"
              aria-label={i18n.t(
                'crm.features.features.notes.components.notesharemodal.поделиться_заметкой',
              )}
              icon={<ShareAltOutlined />}
              onClick={() => setSharingNote(note)}
            />
          </Tooltip>
        ) : null}
        <Tooltip title={i18n.t('crm.features.features.notes.pages.notespage.изменить')}>
          <Button
            size="small"
            type="text"
            aria-label={i18n.t('crm.features.features.notes.pages.notespage.изменить_заметку')}
            icon={<EditOutlined />}
            onClick={() => setEditingNote(note)}
          />
        </Tooltip>
        <Tooltip
          title={
            note.links.length > 0
              ? i18n.t('crm.features.features.notes.pages.notespage.снять_все_привязки')
              : i18n.t('crm.features.features.notes.pages.notespage.привязок_нет')
          }
        >
          <Popconfirm
            title={i18n.t('crm.features.features.notes.pages.notespage.снять_все_привязки_2')}
            description={i18n.t(
              'crm.features.features.notes.pages.notespage.текст_заметки_останется_она_станет_без_при',
            )}
            okText={i18n.t('crm.features.features.notes.pages.notespage.снять')}
            cancelText={i18n.t('crm.features.features.notes.pages.notespage.отмена')}
            disabled={note.links.length === 0}
            onConfirm={() => updateMut.mutate({ id: note.id, input: { links: [] } })}
          >
            <Button
              size="small"
              type="text"
              aria-label={i18n.t(
                'crm.features.features.notes.pages.notespage.снять_все_привязки_3',
              )}
              icon={<DisconnectOutlined />}
              disabled={note.links.length === 0}
            />
          </Popconfirm>
        </Tooltip>
        <EntityHistoryButton
          entity="note"
          entityId={note.id}
          title={i18n.t('crm.features.features.notes.pages.notespage.история_заметки')}
          subject={note.body.slice(0, 80)}
          iconOnly
        />
        <DeleteEntityButton
          iconOnly
          label={i18n.t('crm.features.features.notes.pages.notespage.удалить_заметку')}
          title={i18n.t('crm.features.features.notes.pages.notespage.удалить_заметку_2')}
          description={i18n.t(
            'crm.features.features.notes.pages.notespage.заметка_пропадет_из_списка_и_со_всех_карто',
          )}
          loading={deleteMut.isPending && deleteMut.variables === note.id}
          onConfirm={() => deleteMut.mutate(note.id)}
        />
      </Space>
    ),
  };

  const grid = useGridProfile('notes', NOTE_GRID_COLUMNS);
  const ordered = useOrderedColumns<NoteItem>({
    profile: grid,
    columnMap,
    trailing: [actionsColumn],
  });

  // Минимальная ширина таблицы: фиксированные колонки плюс пол под текст заметки.
  // Общий контракт списков (shared/ui/table-width.ts): длинная заметка обрезается
  // двумя строками и не растягивает таблицу.
  const tableMinWidth = computeTableMinWidth(ordered.columns);

  const mutationError = (updateMut.error ?? deleteMut.error) as Error | null;

  return (
    <div className="animate-fade-in flex h-full flex-col">
      <PageHeader
        title={i18n.t('crm.features.features.notes.pages.notespage.заметки')}
        subtitle={
          <>
            <span data-testid="notes-visible-count">{notes.length}</span>{' '}
            {i18n.tc('crm.counts.notes', notes.length)}
          </>
        }
        actions={
          <Button
            type="primary"
            aria-label={i18n.t('crm.notes.new')}
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
            style={LIST_CREATE_BUTTON_STYLE}
          >
            {i18n.t('crm.notes.new')}
          </Button>
        }
        testId="notes-page-header"
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <FilterBar
          search={
            <FilterSearch
              id="notes-search"
              aria-label={i18n.t('crm.notes.list.search_label')}
              placeholder={i18n.t(
                'crm.features.features.notes.pages.notespage.поиск_по_тексту_заметки',
              )}
              value={q}
              onChange={setQuery}
            />
          }
        >
          <GridColumnsButton profile={grid} />
        </FilterBar>

        <div className="flex flex-col gap-3">
          {mutationError ? (
            <Alert
              type="error"
              showIcon
              closable
              title={i18n.t('crm.features.features.notes.pages.notespage.ошибка_операции')}
              description={mutationError.message}
            />
          ) : null}

          {isError ? (
            <ErrorState
              title={i18n.t(
                'crm.features.features.notes.pages.notespage.не_удалось_загрузить_заметки',
              )}
              error={error}
              onRetry={() => void refetch()}
            />
          ) : isLoading ? (
            <div style={LIST_SURFACE_STYLE}>
              <QuietListSkeleton rows={6} />
            </div>
          ) : notes.length === 0 ? (
            <div style={LIST_SURFACE_STYLE}>
              <QuietEmpty
                text={i18n.t('crm.features.features.notes.pages.notespage.заметок_пока_нет')}
              />
            </div>
          ) : (
            <div className="crm-checkbox-square" style={LIST_SURFACE_STYLE}>
              <EntityTable<NoteItem>
                className="notes-grid"
                tableLayout="fixed"
                scroll={{ x: tableMinWidth }}
                dataSource={notes}
                columns={ordered.columns}
                components={ordered.components}
              />
            </div>
          )}
        </div>
      </div>

      <NoteCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      {editingNote !== null ? (
        <NoteFormModal open note={editingNote} onClose={() => setEditingNote(null)} />
      ) : null}
      {sharingNote !== null ? (
        <NoteShareModal open note={sharingNote} onClose={() => setSharingNote(null)} />
      ) : null}
    </div>
  );
}
